/**
 * AI Chat store 模块共享的类型与常量。
 *
 * <p>本文件不依赖 Pinia / Vue，可被任意模块（timeline / messages / detectors / store）
 * 安全 import。所有公开类型常量都集中在这里，避免循环依赖。</p>
 */
import type {
  AiChatResponse,
  AiToolCallUi,
  AiUserQuestionOptionView
} from '@/api/aiChat'

export type AiMessageRole = 'user' | 'assistant' | 'tool' | 'system'

/** 用户消息中已上传材料的气泡展示（与 materialIds 对应） */
export interface AiUserMaterialAttachment {
  materialId: string
  fileName: string
}

/**
 * 仅附件、无用户正文时发给后端的占位句（满足后端 @NotBlank）。
 * 同回合若存在 {@link AiUserMaterialAttachment}，前端可隐藏该句仅展示文件气泡。
 */
export const AI_CHAT_FILE_ONLY_MESSAGE = '请根据已上传的材料处理。'

/**
 * 异常停止的可继续原因（与后端 AiChatStreamRunner.isResumableRunPhase 同一清单）。
 * run_done.finishReason / stream-state.phase 双来源共用此判定。
 */
export const RESUMABLE_STOP_REASONS = [
  'CONTINUATION_REQUIRED',
  'BUDGET_STOP',
  'ERROR'
] as const

export type ResumableStopReason = (typeof RESUMABLE_STOP_REASONS)[number]

export function isResumableStopReason(reason: string | null | undefined): boolean {
  return !!reason && (RESUMABLE_STOP_REASONS as readonly string[]).includes(reason)
}

/** stream-state 的 phase（如 v2_continuation_required / error）映射为可继续原因；不可继续返回 null。 */
export function resumableReasonFromPhase(
  phase: string | null | undefined
): ResumableStopReason | null {
  const normalized = (phase ?? '').trim().toLowerCase()
  if (normalized === 'v2_continuation_required') return 'CONTINUATION_REQUIRED'
  if (normalized === 'v2_budget_stop') return 'BUDGET_STOP'
  if (normalized === 'error') return 'ERROR'
  return null
}

/** 上一回合异常停止后的可继续状态（提示条数据源）。 */
export interface ResumableStop {
  reason: ResumableStopReason
}

/** 单条助手消息内按时间顺序的片段（推理 → 正文 → 工具 → …） */
export type AiTimelineSegment =
  | { kind: 'reasoning'; text: string }
  | { kind: 'content'; text: string }
  | { kind: 'tool'; call: AiToolCallUi }

/**
 * v1.1.44+ 折叠态支持：正常结束后，前 N-1 轮 replyToUser + 工具调用被折叠为一段，
 * 仅展示『任务耗时 Xm Ys >』入口；点击展开后渲染 segments 列表。
 * 仅当 turn 正常结束（最终 ACTIVE 消息带 taskDurationMs）且存在 ROUND_REPLY 历史时存在。
 */
export interface CollapsedPriorRounds {
  /** 任务耗时（毫秒）—— 来源是最终 ACTIVE 消息的 taskDurationMs */
  durationMs: number
  /** 被折叠的 timeline 段：每轮的 replyToUser + 该轮之后的 tool 调用 */
  segments: AiTimelineSegment[]
}

export interface AiUiMessage {
  id: string
  role: AiMessageRole
  content: string
  /** 用户消息：本回合上传的文件（气泡展示，不写入对话正文） */
  materialAttachments?: AiUserMaterialAttachment[] | null
  reasoningContent?: string | null
  /** 流式与完成后助手消息的时间线展示（与 SSE 顺序一致） */
  timeline?: AiTimelineSegment[] | null
  /** Pending state: assistant turn that is still waiting for the backend reply. */
  pending?: boolean
  /**
   * 流式空档标记：后端发来 `tool_done`（工具执行完，等模型接手，含内部修复/未执行结果）
   * 或 `import_progress` 达到 100%（导入草稿完成）时置 true；
   * 收到任何 `*_delta` / `tool_start` / 进行中的 `import_progress` / 提问·确认卡 /
   * 终态事件（run_done / run_error）时重置为 false。
   * 渲染层据此显示"正在规划下一步…"占位符，无需轮询时间戳。
   * 仅在 pending=true 时有意义；非 pending 消息一律视为 false。
   */
  awaitingNext?: boolean | null
  /**
   * 上下文压缩进行中（compression_started SSE 事件驱动）：后端在上下文达到模型容量
   * 85% 时触发一次压缩（独立总结调用，数秒暂停）。置 true 后底部显示
   * 「正在整理较早的对话记忆…」，收到任何活动事件（`*_delta` / `tool_start` /
   * `import_progress`）时复位；终态后随 pending 失效。
   * 仅在 pending=true 时有意义。
   */
  compressing?: boolean | null
  /**
   * 瞬态导入进度（import_progress SSE 事件驱动）：只驱动进行中的导入芯片文案，
   * 不进底部提示位。工具结束后必须清空，让芯片回到完成/失败，底部才能出现「正在规划下一步」。
   */
  importProgress?: { stage: string; kind?: string | null } | null
  /** Error message when an assistant turn failed. */
  error?: string | null
  /**
   * v1.1.44+ 折叠态支持：正常结束·折叠态时携带本字段；
   * 组件渲染优先级：collapsedPriorRounds 存在 → 渲染折叠入口
   */
  collapsedPriorRounds?: CollapsedPriorRounds | null
  /** v1.1.44+ 异常结束标记：仅当 turn 异常结束（非 COMPLETE_PLAN 收尾）时存在。
   * 正常结束不设置本字段。 */
  runStatus?: 'normal' | 'abnormal' | null
  /** 用户点停止按钮主动终止本回合：气泡底部在重新生成按钮左侧显示「手动终止输出」状态。 */
  stoppedByUser?: boolean | null
  /** role=user 时为 true 表示这是主代理内部桥接文本（非真实用户输入），
   *  前端应隐藏该消息；与后端 AiMessageView.isSystemBridge 对应。 */
  isSystemBridge?: boolean | null
  createdAt: number
}

/** Parsed from tool JSON when Gateway asks for explicit confirmation (P1). */
export interface PendingGatewayConfirmation {
  id: string
  /** Provider tool-call id.  Lets every renderer hide the exact pre-execution call behind this card. */
  toolCallId?: string
  /** 产生该待确认流程的会话；仅在与当前 {@link useAiChatStore#sessionId} 一致时由 UI 展示 */
  conversationSessionId: string
  message: string
  toolName?: string
  displayLabel?: string
  riskLevel?: string
  effectiveRisk?: string
  argumentsJson?: string
  /** Renderer-friendly before/after/impact fields from the preflight report. */
  preview?: Record<string, unknown>
  /** Complete gateway preflight report, used for refresh recovery and detail rendering. */
  preflightReport?: Record<string, unknown>
  impactDescription?: string
  expiresAt?: string
  requiresRiskAcknowledgement?: boolean
  status?: 'PENDING' | 'EXECUTING' | 'EXECUTED' | 'FAILED' | 'CANCELLED'
    | 'REJECTED' | 'EXPIRED' | 'SUPERSEDED' | 'STALE_REJECTED' | 'RECOVERY_REQUIRED'
  error?: string | null
}

export interface PendingUserQuestion {
  id: string
  /** Provider tool-call id for the question that owns this card. */
  toolCallId?: string
  conversationSessionId: string
  question: string
  options: AiUserQuestionOptionView[]
  customOption: {
    id: string
    label: string
    maxLength: number
    placeholder?: string | null
  }
  toolName?: string
  status?: 'PENDING' | 'ANSWERED' | 'SKIPPED' | 'EXPIRED' | 'SUPERSEDED' | string
  /** 卡片 TTL 截止时间（ISO 字符串），用于剩余时间展示；过期 = 用户不在场，非拒绝。 */
  expiresAt?: string
  error?: string | null
  questionGroupId?: string | null
  questionIndex?: number
  questionTotal?: number
}

/**
 * 统一用户行动队列的条目类型（discriminated union）。
 *
 * <p>把"确认卡"（PendingGatewayConfirmation）和"提问卡"（PendingUserQuestion）
 * 通过 {@code type} 字段合并成同一种条目。这样：</p>
 *
 * <ul>
 *   <li>后端提问/确认分轨协调，共用 {@code turnId} 仅用于 SSE 关联</li>
 *   <li>前端用一条统一队列管理显示顺序，队头单显</li>
 *   <li>{@code pending_resolved} 或 {@code /pending} 快照负责收敛已处理项</li>
 * </ul>
 *
 * <p>所有待办都按 FIFO 顺序入队，提问卡的答案相互独立（答完一张会出现下一张）。</p>
 */
export type UserActionConfirmationItem = PendingGatewayConfirmation & { type: 'confirmation' }
export type UserActionQuestionItem = PendingUserQuestion & { type: 'question' }
export type UserActionItem =
  | UserActionConfirmationItem
  | UserActionQuestionItem

export interface ChatStreamAccumulator {
  donePayload: AiChatResponse | null
  doneProcessed: boolean
  streamErrored: boolean
  /** Journal identity and last observed sequence for v2 SSE recovery. */
  v2RunId?: string | null
  v2LastSeq?: number
  /**
   * Session that owns this in-flight SSE. Events must keep writing here even if the
   * user switched to another thread (including a blank「新对话」draft).
   */
  ownerSessionId?: string | null
  /**
   * 该流在 streamsBySession 注册表中的当前 key。服务端 run_start/done 重定向
   * 归属会话时用于迁移注册条目，保证收尾清理与 has() 查询的 key 一致。
   */
  registeredSessionId?: string | null
}

/**
 * 服务端消息视图类型别名（来自 API 包）。这里 re-export 是为了避免在
 * 上层模块里频繁写 `import { AiToolCallView } from '@/api/aiChat'`。
 */
export type {
  AiChatResponse,
  AiMessageView,
  AiRecallUserMessageResponse,
  AiStreamStateView,
  AiToolCallUi,
  AiToolCallView,
  AiUserQuestionOptionView
} from '@/api/aiChat'

/** 生成客户端 UI 消息 id（不参与服务端持久化） */
export function genMessageId(): string {
  return `m-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
}

/** 将 {@code srv-123} 用户气泡 id 解为服务端消息主键（不含合并助手气泡 id） */
export function parseNumericUserMessageId(msg: AiUiMessage): number | null {
  if (msg.role !== 'user') {
    return null
  }
  if (!msg.id.startsWith('srv-')) {
    return null
  }
  const rest = msg.id.slice(4)
  if (rest.includes('-')) {
    return null
  }
  const n = Number(rest)
  return Number.isFinite(n) ? n : null
}
